
import { AtlasEntry, AtlasCategory, BlogPost, SystemLog, VirtualDirectory } from '../types';

// --- CONFIGURAZIONE COSTANTI ---
// ID del modulo Contact Form 7 su WordPress
const WP_CONTACT_FORM_ID = '8950c90'; 

// --- CONFIGURAZIONE WORDPRESS HEADLESS ---
// Variabili dinamiche configurabili dalla UI
let wpConfig = {
    API_URL: 'https://www.cyberumanita.com/wp/wp-json/wp/v2',
    AUTH_USER: '',
    AUTH_PASS: ''
};

// Funzione per aggiornare la configurazione a runtime (dalla UI Admin)
export const updateWordPressConfig = (url: string, user: string, pass: string) => {
    // Assicuriamoci che l'URL non finisca con /
    const cleanUrl = url.replace(/\/$/, "");
    // Se l'utente inserisce solo il dominio base, aggiungiamo il path API standard
    const finalUrl = cleanUrl.includes('/wp-json') ? cleanUrl : `${cleanUrl}/wp-json/wp/v2`;
    
    wpConfig = {
        API_URL: finalUrl,
        AUTH_USER: user,
        AUTH_PASS: pass
    };
    console.log("Configurazione WP Aggiornata:", wpConfig.API_URL);
};

// --- SITE INFO FETCHER (Logo & Name) ---
export const fetchSiteInfo = async (): Promise<{ name: string, description: string, logo_url: string | null }> => {
    try {
        const baseUrl = wpConfig.API_URL.replace('/wp/v2', '');
        const customEndpoint = `${baseUrl}/cyberumanita/v1/info`;
        
        const res = await fetch(customEndpoint);
        if (res.ok) {
            return await res.json();
        }
        throw new Error("Custom endpoint not found");
    } catch (e) {
        console.warn("Could not fetch custom site info, falling back to defaults.");
        return {
            name: 'CYBERUMANITÀ',
            description: 'Atlante Digitale',
            logo_url: null
        };
    }
};

// --- CONTACT FORM HANDLER (Production Ready) ---
export const sendContactMessage = async (
    formData: { name: string; email: string; subject: string; message: string }
): Promise<boolean> => {
    
    // Calcola URL base (rimuove /wp/v2 per accedere al namespace di CF7)
    const baseUrl = wpConfig.API_URL.replace('/wp/v2', '');
    const endpoint = `${baseUrl}/contact-form-7/v1/contact-forms/${WP_CONTACT_FORM_ID}/feedback`;

    try {
        const body = new FormData();
        // MAPPING CAMPI CF7
        // [text* your-name]
        body.append('your-name', formData.name);
        // [email* your-email]
        body.append('your-email', formData.email);
        // [text* your-subject]
        body.append('your-subject', formData.subject);
        // [textarea* your-message]
        body.append('your-message', formData.message);
        
        // Campi tecnici CF7 per validazione
        body.append('_wpcf7_unit_tag', 'rte');

        // Invio richiesta
        const res = await fetch(endpoint, {
            method: 'POST',
            body: body
        });
        
        if (!res.ok) {
            console.error(`Contact Form Error (ID: ${WP_CONTACT_FORM_ID}). Status: ${res.status}`);
            return false;
        }

        const json = await res.json();
        if (json.status === 'mail_sent') {
            return true;
        } else {
            console.warn("CF7 Validation Error:", json);
            return false;
        }
    } catch (e) {
        console.error("Contact Form connection failed", e);
        return false; 
    }
};

// --- STRUTTURA DATI CISO-ORIENTED (ENCICLOPEDIA COMPLETA) ---
export interface CisoEntryDetail {
    header: {
        id: string;
        title: string;
        type: 'Minaccia' | 'Vulnerabilità' | 'Controllo' | 'Concetto' | 'Normativa' | 'Modello AI';
        riskCurrent: { label: string; score: number; max: number };
        exposure: number;
        coverage: number;
        owner: string;
        lastUpdated: string;
        version: string;
    };
    genesis: { // ARCHÈ
        etymology: string;
        definition: string;
        archetype: string; 
        history: { year: string; event: string }[];
    };
    overview: {
        impactMap: { process: string; type: string; criticality: number }[];
        worstCaseScenario: { title: string; description: string; cost: string };
        rolesInvolved: string[];
    };
    risk: { // TECHNE - RISK
        snapshot: { intrinsic: string; residual: string; confidence: string; trend: 'up' | 'stable' | 'down' };
        matrixScenarios: { title: string; prob: number; impact: number; type: string }[];
        attackSurface: { usersExposed: number; systemsExposed: number; topGap: string };
        financials: { 
            annualLossExpectancy: string; 
            preventionCost: string;
            roi: string;
        };
        decisionBoard: {
            action: 'MITIGATE' | 'ACCEPT' | 'TRANSFER' | 'AVOID';
            rationale: string;
            budget: string;
            horizon: string;
        };
    };
    ops: { // TECHNE - OPS
        threatModel: { actors: string[]; killChain: string[] };
        redTeam: { useCase: string; entryPoint: string; successRate: number }[];
        blueTeam: { visibility: string; detectionRule: string; type: string; coverage: number }[];
        mitre: string[];
        iocs: { type: 'HASH' | 'IP' | 'DOMAIN' | 'PATTERN' | 'AUDIT'; value: string; confidence: 'High' | 'Med' | 'Low' | 'Critical' }[];
        defensiveStack: { tool: string; function: string; status: 'Active' | 'Gap' | 'Partial' | 'Plan' }[];
    };
    governance: { // ETHOS
        humanImpact: string; 
        ethicalDilemma: string; 
        complianceMapping: { framework: string; requirement: string; status: 'OK' | 'Partial' | 'KO' | 'Pending' | 'In Progress' }[];
        auditChecklist: { 
            question: string; 
            severity: 'High' | 'Med' | 'Critical' | 'Low'; 
            status: boolean; 
            remediation: string;
        }[];
        maturityScore: { policy: number; tech: number; people: number };
    };
    resources: {
        relatedTopics: { id: string; title: string; type: string }[];
        artefacts: { title: string; type: 'PDF' | 'DOC' | 'SCRIPT'; size: string }[];
    };
}

const DB_STORAGE_KEY = 'cyberumanita_db_v6_expanded';

// --- DATABASE STATICO DI FALLBACK (Se WP è offline) ---
const PRELOADED_DATABASE: CisoEntryDetail[] = [
    {
        header: { id: "CU_ID_001", title: "Phishing", type: "Minaccia", riskCurrent: { label: "Critical", score: 4.2, max: 5 }, exposure: 68, coverage: 54, owner: "SecOps", lastUpdated: "2024-10-24", version: "4.1" },
        genesis: {
            etymology: "Da 'fishing' (pescare) + 'ph' (phreaking).",
            definition: "Tecnica di ingegneria sociale che utilizza comunicazioni fraudolente per indurre le vittime a rivelare informazioni sensibili.",
            archetype: "Il Trickster (L'Ingannatore)",
            history: [{ year: "1995", event: "AOHell" }, { year: "2016", event: "DNC Hack" }]
        },
        overview: {
            impactMap: [{ process: "Finance", type: "Frode", criticality: 5 }],
            worstCaseScenario: { title: "Ransomware Entry", description: "Credential harvesting leading to domain admin compromise.", cost: "€ 2.4M" },
            rolesInvolved: ["CISO", "HR", "Legal"]
        },
        risk: {
            snapshot: { intrinsic: "Critical", residual: "High", confidence: "High", trend: "up" },
            matrixScenarios: [{ title: "Mass Spam", prob: 5, impact: 3, type: "Ops" }],
            attackSurface: { usersExposed: 85, systemsExposed: 40, topGap: "No Hardware MFA" },
            financials: { annualLossExpectancy: "DYNAMIC", preventionCost: "€ 80k", roi: "460%" },
            decisionBoard: { action: "MITIGATE", rationale: "Vettore primario per ransomware.", budget: "€ 50k", horizon: "3 Mesi" }
        },
        ops: {
            threatModel: { actors: ["Cybercrime"], killChain: ["Delivery"] },
            redTeam: [{ useCase: "Spear Phishing", entryPoint: "Email", successRate: 45 }],
            blueTeam: [{ visibility: "SEG", detectionRule: "DMARC Fail", type: "Signature", coverage: 90 }],
            mitre: ["T1566"],
            iocs: [{ type: 'DOMAIN', value: 'login-microsoft-verify.com', confidence: 'High' }],
            defensiveStack: [{ tool: "Proofpoint", function: "SEG", status: "Active" }]
        },
        governance: {
            humanImpact: "Erosione fiducia digitale.",
            ethicalDilemma: "Eticità dei test di phishing sui dipendenti?",
            complianceMapping: [{ framework: "GDPR", requirement: "Art. 32", status: "OK" }],
            auditChecklist: [{ question: "Simulazioni attive?", severity: "High", status: false, remediation: "Attivare" }],
            maturityScore: { policy: 4, tech: 3, people: 2 }
        },
        resources: { relatedTopics: [], artefacts: [] }
    }
];

// --- SMART HYDRATOR (PROCEDURAL GENERATION) ---
// Now Exported for use in creating new entries
export const smartHydrateEntry = (termTitle: string): CisoEntryDetail => {
    const titleLower = termTitle.toLowerCase();
    
    let category: AtlasEntry['category'] = AtlasCategory.CONCEPT;
    if (/(attack|hack|virus|malware|ransom|phish|threat|exploit|injection|ddos|botnet|apt)/i.test(titleLower)) category = AtlasCategory.THREAT;
    else if (/(defense|security|wall|shield|auth|mfa|encryp|backup|soc|siem|edr)/i.test(titleLower)) category = AtlasCategory.DEFENSE;
    else if (/(law|act|regulat|gdpr|iso|nist|complian)/i.test(titleLower)) category = AtlasCategory.REGULATION;
    else if (/(ai|model|gpt|llm|neural|algorithm)/i.test(titleLower)) category = AtlasCategory.AI_MODEL;

    const isThreat = category === AtlasCategory.THREAT;
    const isDef = category === AtlasCategory.DEFENSE;
    const randomId = Math.floor(Math.random() * 90000) + 10000;
    
    const typeLabel = isThreat ? 'Minaccia' : isDef ? 'Controllo' : category === AtlasCategory.REGULATION ? 'Normativa' : category === AtlasCategory.AI_MODEL ? 'Modello AI' : 'Concetto';
    
    return {
        header: {
            id: `AUTO_${randomId}`,
            title: termTitle,
            type: typeLabel as any,
            riskCurrent: { 
                label: isThreat ? "High" : "Low", 
                score: isThreat ? 4.0 : 2.0, 
                max: 5 
            },
            exposure: isThreat ? 75 : 10,
            coverage: isThreat ? 25 : 80,
            owner: "Admin",
            lastUpdated: new Date().toISOString().split('T')[0],
            version: "1.0"
        },
        genesis: {
            etymology: "",
            definition: `Descrizione per ${termTitle}...`,
            archetype: "N/A",
            history: []
        },
        overview: {
            impactMap: [],
            worstCaseScenario: {
                title: "Scenario Non Definito",
                description: "Descrivi cosa accadrebbe nel caso peggiore.",
                cost: "TBD"
            },
            rolesInvolved: []
        },
        risk: {
            snapshot: { intrinsic: "Med", residual: "Med", confidence: "Low", trend: "stable" },
            matrixScenarios: [],
            attackSurface: { usersExposed: 0, systemsExposed: 0, topGap: "-" },
            financials: { annualLossExpectancy: "-", preventionCost: "-", roi: "-" },
            decisionBoard: { action: "ACCEPT", rationale: "-", budget: "0", horizon: "-" }
        },
        ops: {
            threatModel: { actors: [], killChain: [] },
            redTeam: [],
            blueTeam: [],
            mitre: [],
            iocs: [],
            defensiveStack: []
        },
        governance: {
            humanImpact: "",
            ethicalDilemma: "",
            complianceMapping: [],
            auditChecklist: [],
            maturityScore: { policy: 1, tech: 1, people: 1 }
        },
        resources: { relatedTopics: [], artefacts: [] }
    };
};

export const createEmptyEntry = () => smartHydrateEntry("BOZZA_NUOVA");

// --- DATA ACCESS LAYER ---

const getAuthHeader = () => {
    if (!wpConfig.AUTH_USER || !wpConfig.AUTH_PASS) return {};
    return {
        'Authorization': 'Basic ' + btoa(wpConfig.AUTH_USER + ':' + wpConfig.AUTH_PASS)
    };
};

export const fetchAtlasData = async (): Promise<AtlasEntry[]> => {
    const mergedMap = new Map<string, CisoEntryDetail>();

    // A. Statico
    PRELOADED_DATABASE.forEach(e => mergedMap.set(e.header.title.toLowerCase(), e));

    // B. WP
    try {
        const response = await fetch(`${wpConfig.API_URL}/atlas_entry?per_page=100`);
        if (response.ok) {
            const wpPosts = await response.json();
            const wpEntries = wpPosts
                .filter((p: any) => p.atlas_full_data)
                .map((p: any) => p.atlas_full_data);
            wpEntries.forEach((e: CisoEntryDetail) => mergedMap.set(e.header.title.toLowerCase(), e));
        }
    } catch (e) {}

    // C. LocalStorage (Overrides)
    const stored = localStorage.getItem(DB_STORAGE_KEY);
    if (stored) {
        try {
            const localEntries: CisoEntryDetail[] = JSON.parse(stored).entries || [];
            localEntries.forEach(e => mergedMap.set(e.header.title.toLowerCase(), e));
        } catch(e) {}
    }

    return Array.from(mergedMap.values()).map(e => ({
        id: e.header.id,
        title: e.header.title,
        category: e.header.type === 'Minaccia' ? AtlasCategory.THREAT : 
                  e.header.type === 'Controllo' ? AtlasCategory.DEFENSE : 
                  e.header.type === 'Normativa' ? AtlasCategory.REGULATION : 
                  e.header.type === 'Modello AI' ? AtlasCategory.AI_MODEL : AtlasCategory.CONCEPT,
        shortDescription: e.genesis.definition,
        tags: ['core'], 
        severity: e.header.riskCurrent.label as any
    }));
};

export const fetchEntryDetails = async (termTitle: string): Promise<CisoEntryDetail> => {
    const termLower = termTitle.toLowerCase();
    
    // A. Local
    const stored = localStorage.getItem(DB_STORAGE_KEY);
    if (stored) {
        try {
            const localEntries: CisoEntryDetail[] = JSON.parse(stored).entries || [];
            const foundLocal = localEntries.find(e => e.header.title.toLowerCase() === termLower);
            if (foundLocal) return foundLocal;
        } catch(e) {}
    }

    // B. WP
    try {
        const response = await fetch(`${wpConfig.API_URL}/atlas_entry?search=${encodeURIComponent(termTitle)}`);
        if (response.ok) {
            const data = await response.json();
            if (data.length > 0 && data[0].atlas_full_data) {
                return data[0].atlas_full_data;
            }
        }
    } catch (e) {}

    // C. Static
    const foundStatic = PRELOADED_DATABASE.find(e => e.header.title.toLowerCase() === termLower);
    if (foundStatic) return foundStatic;

    // D. Auto-Gen
    return smartHydrateEntry(termTitle);
};

export const saveEntryToWordPress = async (entry: CisoEntryDetail): Promise<boolean> => {
    
    // SAVE TO LOCAL STORAGE FIRST
    try {
        const stored = localStorage.getItem(DB_STORAGE_KEY);
        let db = stored ? JSON.parse(stored) : { entries: [] };
        
        const existingIdx = db.entries.findIndex((e: any) => e.header.title.toLowerCase() === entry.header.title.toLowerCase());
        
        if (existingIdx >= 0) {
            db.entries[existingIdx] = entry; // Update
        } else {
            db.entries.push(entry); // Insert
        }
        
        localStorage.setItem(DB_STORAGE_KEY, JSON.stringify(db));
    } catch(e) {
        console.error("Local Save Failed", e);
    }

    // SYNC TO WP IF AUTH
    if (!wpConfig.AUTH_USER) return true;

    try {
        const searchRes = await fetch(`${wpConfig.API_URL}/atlas_entry?search=${encodeURIComponent(entry.header.title)}`, {
            headers: getAuthHeader()
        });
        const searchData = await searchRes.json();
        const existingId = searchData.length > 0 ? searchData[0].id : null;

        const url = existingId ? `${wpConfig.API_URL}/atlas_entry/${existingId}` : `${wpConfig.API_URL}/atlas_entry`;
        
        const payload = {
            title: entry.header.title,
            content: entry.genesis.definition,
            status: 'publish',
            atlas_full_data: entry
        };

        const saveRes = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify(payload)
        });

        return saveRes.ok;

    } catch (e) {
        console.error("WP Sync Error", e);
        return false;
    }
};

export const deleteAtlasEntry = async (title: string): Promise<boolean> => {
    const termLower = title.toLowerCase();
    try {
        const stored = localStorage.getItem(DB_STORAGE_KEY);
        if (stored) {
            const db = JSON.parse(stored);
            const initialLen = db.entries.length;
            db.entries = db.entries.filter((e: CisoEntryDetail) => e.header.title.toLowerCase() !== termLower);
            
            if (db.entries.length !== initialLen) {
                localStorage.setItem(DB_STORAGE_KEY, JSON.stringify(db));
            }
        }
    } catch(e) {
        return false;
    }

    if (wpConfig.AUTH_USER) {
        try {
            const searchRes = await fetch(`${wpConfig.API_URL}/atlas_entry?search=${encodeURIComponent(title)}`, {
                headers: getAuthHeader()
            });
            const searchData = await searchRes.json();
            if (searchData.length > 0) {
                const idToDelete = searchData[0].id;
                await fetch(`${wpConfig.API_URL}/atlas_entry/${idToDelete}`, {
                    method: 'DELETE',
                    headers: getAuthHeader()
                });
            }
        } catch (e) {}
    }
    
    return true;
};

// --- IMPORT / EXPORT / TEMPLATES ---

export const downloadBatchTemplate = (): void => {
    const templateData = {
        meta: {
            title: "Cyberumanità Atlas Import File",
            version: "1.0",
            instructions: "Compila l'array 'entries'."
        },
        entries: [
             {
                header: { id: "NEW_001", title: "Esempio Titolo", type: "Minaccia", riskCurrent: { label: "High", score: 3.8, max: 5 }, exposure: 70, coverage: 20, owner: "CISO", lastUpdated: "2024-12-01", version: "1.0" },
                genesis: { etymology: "...", definition: "...", archetype: "...", history: [] },
                overview: { impactMap: [], worstCaseScenario: { title: "...", description: "...", cost: "..." }, rolesInvolved: [] },
                risk: { snapshot: { intrinsic: "High", residual: "High", confidence: "Med", trend: "up" }, matrixScenarios: [], attackSurface: { usersExposed: 0, systemsExposed: 0, topGap: "" }, financials: { annualLossExpectancy: "", preventionCost: "", roi: "" }, decisionBoard: { action: "MITIGATE", rationale: "", budget: "", horizon: "" } },
                ops: { threatModel: { actors: [], killChain: [] }, redTeam: [], blueTeam: [], mitre: [], iocs: [], defensiveStack: [] },
                governance: { humanImpact: "", ethicalDilemma: "", complianceMapping: [], auditChecklist: [], maturityScore: { policy: 0, tech: 0, people: 0 } },
                resources: { relatedTopics: [], artefacts: [] }
            }
        ]
    };

    const blob = new Blob([JSON.stringify(templateData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cyberumanita_import_template.json`;
    a.click();
    window.URL.revokeObjectURL(url);
};

export const importDatabaseFromJSON = async (jsonData: any): Promise<{ success: boolean, count: number, message: string }> => {
    try {
        if (!jsonData || !jsonData.entries || !Array.isArray(jsonData.entries)) {
            return { success: false, count: 0, message: "Formato file non valido." };
        }

        const newEntries = jsonData.entries as CisoEntryDetail[];
        const dbStored = localStorage.getItem(DB_STORAGE_KEY);
        
        // OPTIMIZED OVERWRITE LOGIC USING MAP (O(N) instead of O(N^2))
        // 1. Load existing entries into a Map
        const entryMap = new Map<string, CisoEntryDetail>();
        if (dbStored) {
            const parsed = JSON.parse(dbStored);
            if(parsed.entries) {
                parsed.entries.forEach((e: CisoEntryDetail) => {
                    entryMap.set(e.header.title.toLowerCase(), e);
                });
            }
        }

        // 2. Overwrite or Add new entries
        let addedCount = 0;
        let wpSuccessCount = 0;

        for (const entry of newEntries) {
            // This guarantees overwrite if title exists
            entryMap.set(entry.header.title.toLowerCase(), entry);
            addedCount++;

            if (wpConfig.AUTH_USER) {
                const synced = await saveEntryToWordPress(entry);
                if (synced) wpSuccessCount++;
            }
        }

        // 3. Save back to array
        const finalEntries = Array.from(entryMap.values());
        localStorage.setItem(DB_STORAGE_KEY, JSON.stringify({ entries: finalEntries }));
        
        window.dispatchEvent(new Event('atlas-update'));

        let msg = `Processati ${addedCount} elementi. Database aggiornato (Totale: ${finalEntries.length}).`;
        if (wpConfig.AUTH_USER) {
            msg += ` Sincronizzati su WordPress: ${wpSuccessCount}/${addedCount}.`;
        }

        return { success: true, count: addedCount, message: msg };

    } catch (e) {
        console.error("Import error", e);
        return { success: false, count: 0, message: "Errore durante il parsing del file JSON." };
    }
};

export const exportDatabaseToJSON = (): void => {
    fetchAtlasData().then(async (list) => {
        const fullDetails = [];
        for (const item of list) {
            const detail = await fetchEntryDetails(item.title);
            fullDetails.push(detail);
        }
        
        const blob = new Blob([JSON.stringify({ entries: fullDetails }, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cyberumanita_full_backup.json`;
        a.click();
        window.URL.revokeObjectURL(url);
    });
};

export const generateWordPressXML = (entry: any): void => {
    const xmlContent = `<?xml version="1.0" encoding="UTF-8" ?>
<rss version="2.0"
    xmlns:excerpt="http://wordpress.org/export/1.2/excerpt/"
    xmlns:content="http://purl.org/rss/1.0/modules/content/"
    xmlns:wfw="http://wellformedweb.org/CommentAPI/"
    xmlns:dc="http://purl.org/dc/elements/1.1/"
    xmlns:wp="http://wordpress.org/export/1.2/"
>
<channel>
    <title>Cyberumanità Export</title>
    <item>
        <title><![CDATA[${entry.header.title}]]></title>
        <content:encoded><![CDATA[${JSON.stringify(entry, null, 2)}]]></content:encoded>
        <wp:post_type><![CDATA[atlas_entry]]></wp:post_type>
        <wp:status><![CDATA[publish]]></wp:status>
    </item>
</channel>
</rss>`;

    const blob = new Blob([xmlContent], { type: 'text/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entry.header.title.replace(/\s+/g, '_')}_wp_export.xml`;
    a.click();
    window.URL.revokeObjectURL(url);
};

export const getVirtualDirectories = async (): Promise<VirtualDirectory[]> => {
    const data = await fetchAtlasData();
    const dirs: Record<string, AtlasEntry[]> = {};
    data.forEach(entry => {
        const letter = entry.title.charAt(0).toUpperCase();
        if (!dirs[letter]) dirs[letter] = [];
        dirs[letter].push(entry);
    });
    return Object.keys(dirs).sort().map(letter => ({
        name: letter,
        files: dirs[letter].sort((a,b) => a.title.localeCompare(b.title))
    }));
};

// --- REAL WORDPRESS ARTICLES FETCHING WITH STATIC FALLBACK ---

// STATIC ARTICLE DATABASE (FALLBACK)
const STATIC_ARTICLES: BlogPost[] = [
    {
        id: 1,
        category: "Etica AI",
        title: "Quando un algoritmo decide: chi risponde davvero?",
        date: "25 Maggio 2024",
        author: "mind@cyberumanita.com",
        readTime: "3 min",
        excerpt: "C’è un momento, quasi impercettibile, in cui smettiamo di scegliere e iniziamo ad essere scelti...",
        content: `
            <p>C’è un momento, quasi impercettibile, in cui smettiamo di scegliere e iniziamo ad essere scelti. Non sempre per cattiveria, spesso per comodità. Un algoritmo suggerisce, ordina, filtra, valuta. A volte previene un rischio; a volte lo crea. A volte decide in modo neutro; a volte replica bias, obiettivi opachi, interessi che non ci appartengono. La domanda, allora, non è solo “cosa decide”, ma “chi risponde”.</p>
            <p>Per anni abbiamo raccontato la tecnologia come uno strumento. Poi lo strumento ha iniziato a ottimizzare. Poi a prevedere. Poi a intervenire. Oggi, in molti contesti, l’algoritmo non è più un martello, è un consulente silenzioso. Se sbaglia, chi paga il prezzo? La persona? L’azienda? Il programmatore? Il dato? La società intera?</p>
            <p>La responsabilità non è un dettaglio legale; è una struttura morale. Significa poter ricostruire il perché di una scelta, capire quali dati sono stati usati, quali obiettivi erano impliciti, quali errori erano possibili. Significa progettare sistemi in cui la decisione automatica non diventa un alibi. “Lo ha detto l’AI” non è una spiegazione. È una fuga.</p>
            <p>Cyberumanità parte da qui: la decisione non è mai solo tecnica. Ogni sistema intelligente porta dentro una visione del mondo. Anche quando sembra neutro, sta scegliendo cosa privilegiare: velocità o accuratezza, efficienza o equità, controllo o libertà. E se scegli una metrica, stai già scegliendo un’etica.</p>
            <p>La risposta non è rifiutare l’algoritmo. È metterlo al suo posto: strumento potente, sì; ma governato. Trasparenza, auditabilità, supervisione umana, responsabilità dichiarata. Un sistema che decide deve anche poter essere interrogato. E chi lo mette in produzione deve assumersi il peso delle sue conseguenze.</p>
            <p>Perché il futuro non è un algoritmo. È una responsabilità umana.</p>
        `
    },
    {
        id: 2,
        category: "Cultura",
        title: "La sicurezza come cultura: perché la cybersecurity non è solo IT",
        date: "20 Maggio 2024",
        author: "Redazione",
        readTime: "4 min",
        excerpt: "Molti pensano che la cybersecurity sia una lista di cose da comprare. Poi arriva l’incidente...",
        content: `
            <p>Molti pensano che la cybersecurity sia una lista di cose da comprare: firewall, antivirus, backup, MFA. Tutto utile. Poi arriva l’incidente e scopri che il problema era una mail, un clic, un’abitudine. La sicurezza non fallisce solo nei server; fallisce nei comportamenti. E i comportamenti non si patchano con un aggiornamento.</p>
            <p>Cybersecurity è cultura perché è una forma di attenzione. È sapere come funziona ciò che usiamo, dove sono i confini, quali promesse sono reali e quali sono marketing. È smettere di trattare la tecnologia come magia e ricominciare a trattarla come ambiente. E ogni ambiente richiede regole, mappe, prudenza.</p>
            <p>Una cultura della sicurezza non è paranoica. È lucida. Non vive di paura, vive di consapevolezza. La paura immobilizza, la consapevolezza orienta. Quando capisci come si muove una truffa, come si costruisce un raggiro, come si sfrutta una debolezza, inizi a vedere i segnali. Non diventi invulnerabile, diventi competente.</p>
            <p>La cultura della sicurezza è anche un atto di rispetto verso gli altri. Perché ogni account compromesso è una porta aperta che può diventare un danno collettivo. Ogni disattenzione personale può trasformarsi in un problema per una famiglia, un’azienda, un ospedale, una città. La sicurezza è un bene comune, come l’acqua pulita o l’aria respirabile: te ne accorgi quando manca.</p>
            <p>Cyberumanità vuole ridare alla cybersecurity la sua dimensione umana: non solo difesa, ma educazione; non solo tecnologia, ma linguaggio. Se non sai spiegare un rischio in modo chiaro, non lo stai davvero gestendo. Stai solo sperando.</p>
            <p>E sperare non è una strategia.</p>
        `
    },
    {
        id: 3,
        category: "Deepfake",
        title: "AI e verità: riconoscere il verosimile dal vero nell’era dei deepfake",
        date: "15 Maggio 2024",
        author: "mind@cyberumanita.com",
        readTime: "3 min",
        excerpt: "Prima avevamo un problema: le bugie. Oggi abbiamo un problema più sottile: le bugie plausibili.",
        content: `
            <p>Prima avevamo un problema: le bugie. Oggi abbiamo un problema più sottile: le bugie plausibili. Il deepfake non è solo falsificazione. È una forma di erosione della fiducia. Perché non colpisce soltanto ciò che è vero, colpisce ciò che è credibile.</p>
            <p>Il verosimile è pericoloso perché somiglia al vero abbastanza da passare il confine senza farsi notare. Una voce “quasi” identica, un volto “quasi” perfetto, un video “quasi” coerente. L’effetto non è solo inganno; è stanchezza. Dopo un po’ smetti di verificare, o smetti di credere. Entrambe le cose fanno male.</p>
            <p>Nell’era dei contenuti generati, la difesa più forte non è la diffidenza totale. È l’alfabetizzazione. Saper leggere i contesti, controllare le fonti, riconoscere l’urgenza artificiale (“è successo ora, condividi subito”), distinguere prova da narrazione. La tecnologia accelera, ma la verità richiede tempo.</p>
            <p>Cyberumanità non demonizza l’AI. La stessa tecnologia che genera falsi può anche aiutare a rilevarli. Ma c’è un punto che nessun modello risolve al posto nostro: il bisogno umano di senso. La disinformazione funziona quando tocca emozioni profonde, quando offre una storia facile, quando ti fa sentire parte di qualcosa. La verità, spesso, è più lenta e meno gratificante.</p>
            <p>Il compito culturale, oggi, è proteggere lo spazio del dubbio sano. Non lo scetticismo cinico, ma la domanda onesta. “Come lo so?” “Chi lo dice?” “Che prove ho?” Sono domande che salvano più di mille tool.</p>
            <p>Perché se perdiamo la capacità di distinguere, perdiamo anche la libertà di scegliere.</p>
        `
    },
    {
        id: 4,
        category: "Privacy",
        title: "Privacy non è segretezza: è libertà di scelta",
        date: "10 Maggio 2024",
        author: "Redazione",
        readTime: "3 min",
        excerpt: "Privacy non significa “non ho nulla da nascondere”. Significa “ho qualcosa da decidere”.",
        content: `
            <p>Privacy non significa “non ho nulla da nascondere”. Significa “ho qualcosa da decidere”. La privacy è il diritto di avere una zona di autonomia in cui la persona non è un prodotto. Non è una difesa per i colpevoli; è un confine per i cittadini.</p>
            <p>La segretezza è chiudere. La privacy è governare. È poter stabilire chi vede cosa, quando, per quanto tempo, per quale scopo. È la differenza tra una casa con la porta e una casa con le pareti di vetro. Anche se non stai facendo nulla di male, vivere senza porta cambia il modo in cui vivi.</p>
            <p>Nel digitale, la privacy è spesso ridotta a un banner. Un clic frettoloso, una formula legale, un consenso estorto dalla fretta. Ma la libertà non dovrebbe essere un’interfaccia progettata per farti cedere. Dovrebbe essere una scelta comprensibile, proporzionata, revocabile.</p>
            <p>Cyberumanità parla di privacy come dignità, perché i dati non sono solo numeri. Sono tracce: abitudini, fragilità, desideri, identità. Trattare il dato con rispetto significa trattare la persona con rispetto. E una società che normalizza la sorveglianza normalizza anche l’abuso.</p>
            <p>Non serve diventare ossessivi. Serve diventare consapevoli. Privacy è un muscolo civico: si allena con piccole decisioni, con domande semplici, con un’idea chiara del valore della propria autonomia.</p>
            <p>Perché la libertà non si perde tutta in un giorno. Si cede a rate.</p>
        `
    },
    {
        id: 5,
        category: "Ethics",
        title: "Etica “by design”: come si costruisce una tecnologia che merita fiducia",
        date: "05 Maggio 2024",
        author: "mind@cyberumanita.com",
        readTime: "4 min",
        excerpt: "L’etica non è un sermone dopo il lancio. È una scelta prima della prima riga di codice.",
        content: `
            <p>L’etica non è un sermone dopo il lancio. È una scelta prima della prima riga di codice. “By design” significa che la responsabilità è nella struttura, non nella comunicazione. Non basta dire “abbiamo valori”. Bisogna tradurli in vincoli, procedure, controlli, trasparenza.</p>
            <p>Una tecnologia che merita fiducia è quella che non ti costringe ad avere fede. Ti permette di verificare. Non ti chiede di credere, ti offre evidenze: come gestisce i dati, come riduce i rischi, come risponde agli incidenti, come limita i danni quando qualcosa va storto.</p>
            <p>Etica by design significa anche progettare per l’errore umano. Perché l’errore accade. E la differenza tra un sistema sano e uno fragile è come gestisce l’inevitabile: fallisce in modo sicuro o in modo catastrofico?</p>
            <p>Cyberumanità propone un principio semplice: il valore non è la potenza, è la direzione. Un sistema può essere brillante e dannoso. Può essere efficiente e ingiusto. Può essere “innovativo” e disumano. L’etica serve a ricordare che non tutto ciò che è possibile è desiderabile.</p>
            <p>E che il progresso vero non è fare di più. È fare meglio.</p>
        `
    },
    {
        id: 6,
        category: "Data Rights",
        title: "Il dato come traccia umana: cosa stiamo cedendo quando “accettiamo tutto”",
        date: "01 Maggio 2024",
        author: "Redazione",
        readTime: "3 min",
        excerpt: "Ogni volta che clicchi “accetta”, non stai solo autorizzando un trattamento. Stai cedendo un pezzo di biografia.",
        content: `
            <p>Ogni volta che clicchi “accetta”, non stai solo autorizzando un trattamento. Stai cedendo un pezzo di biografia. Piccolo, quasi invisibile, ma reale. Il dato racconta: dove vai, cosa temi, cosa desideri, quanto dormi, come consumi, chi frequenti. È una geografia dell’anima, ricostruita per inferenza.</p>
            <p>Non è poesia. È matematica applicata alla vita. E proprio perché è matematica, tende a sembrare neutra. Ma neutro non significa innocuo. Un dato isolato è poca cosa; un profilo è potere. Il potere di prevederti, influenzarti, valutarti, escluderti.</p>
            <p>Cyberumanità insiste su un punto: i dati non sono solo materia prima per l’innovazione. Sono materia sensibile. Trattarli bene significa ridurre la raccolta, chiarire gli scopi, minimizzare la conservazione, proteggere l’accesso, rendere le persone consapevoli. Non perché “fa bella figura”, ma perché senza questa cura il digitale diventa estrattivo.</p>
            <p>E un mondo estrattivo, prima o poi, estrae anche ciò che ti rende libero.</p>
        `
    },
    {
        id: 7,
        category: "Social Eng.",
        title: "La mente sotto attacco: ingegneria sociale, persuasione e manipolazione",
        date: "28 Aprile 2024",
        author: "mind@cyberumanita.com",
        readTime: "4 min",
        excerpt: "L’hacker più efficace non buca un firewall. Buca una convinzione.",
        content: `
            <p>L’hacker più efficace non buca un firewall. Buca una convinzione. L’ingegneria sociale è la scienza della pressione: urgenza, paura, autorità, scarsità, confusione. Non è una truffa “stupida”. È una strategia psicologica raffinata, costruita sul fatto che siamo umani, quindi fallibili.</p>
            <p>Quando ricevi una mail “urgente”, quando qualcuno finge un superiore, quando un link sembra normale, l’attacco non è alla macchina. È alla tua attenzione. E l’attenzione è una risorsa finita, soprattutto in un mondo che te la spezza in mille notifiche.</p>
            <p>Difendersi non significa diventare sospettosi di tutto. Significa riconoscere i pattern: richieste fuori contesto, urgenza artificiale, promesse troppo facili, minacce implicite, linguaggio che non torna. Significa costruire abitudini: verificare, richiamare, chiedere conferma su un canale diverso, rallentare.</p>
            <p>Cyberumanità difende la lentezza come misura di sicurezza. La fretta è il miglior alleato dell’attaccante. La lucidità è il tuo.</p>
            <p>Perché la sicurezza, spesso, è un “no” detto con calma.</p>
        `
    },
    {
        id: 8,
        category: "Governance",
        title: "Sicurezza e potere: perché proteggere significa anche limitare qualcuno",
        date: "22 Aprile 2024",
        author: "Redazione",
        readTime: "3 min",
        excerpt: "Ogni sicurezza crea un confine. E ogni confine limita. È inevitabile.",
        content: `
            <p>Ogni sicurezza crea un confine. E ogni confine limita. È inevitabile. La domanda non è se limitare, ma chi limita chi, e per quale scopo. La sicurezza può essere cura, oppure controllo. Può difendere, oppure dominare. La differenza la fa la governance, e la trasparenza.</p>
            <p>Una società che usa la sicurezza come pretesto per sorvegliare tutto non è più sicura. È solo più obbediente. Una tecnologia che promette protezione ma non accetta scrutinio è un rischio travestito da soluzione.</p>
            <p>Cyberumanità propone una visione adulta: sicurezza sì, ma con diritti. Difesa sì, ma con limiti. Protezione sì, ma senza annullare l’autonomia. La sicurezza migliore è quella che non ha bisogno di diventare assoluta, perché sa di vivere dentro una democrazia, non sopra di essa.</p>
            <p>Il punto è questo: proteggere non deve significare trasformare le persone in sospetti permanenti. La fiducia è fragile. Una volta rotta, nessun firewall la ripara.</p>
        `
    },
    {
        id: 9,
        category: "Philosophy",
        title: "Umanesimo digitale: come restare umani mentre tutto diventa automatizzabile",
        date: "18 Aprile 2024",
        author: "mind@cyberumanita.com",
        readTime: "3 min",
        excerpt: "L’automazione promette sollievo: meno fatica, meno errori, più efficienza. E spesso è vero...",
        content: `
            <p>L’automazione promette sollievo: meno fatica, meno errori, più efficienza. E spesso è vero. Ma ogni delega cambia anche chi delega. Se deleghi troppo, perdi competenze. Se deleghi senza capire, perdi autonomia. Se deleghi senza limiti, perdi responsabilità.</p>
            <p>Restare umani non significa rifiutare l’AI. Significa mantenere vivi tre gesti: comprendere, scegliere, rispondere. Comprendere come funziona ciò che usi. Scegliere quando usarlo e quando no. Rispondere delle conseguenze, senza scaricarle sul sistema.</p>
            <p>Cyberumanità parla di umanesimo digitale come di una postura: un modo di stare nel presente senza farsi inghiottire. Significa proteggere la profondità contro il rumore, la qualità contro la quantità, la relazione contro la sola ottimizzazione.</p>
            <p>Perché l’umano non è il contrario della macchina. È ciò che dà senso al suo uso.</p>
        `
    },
    {
        id: 10,
        category: "Policy",
        title: "Dal rischio alla responsabilità: un nuovo patto tra cittadini, imprese e AI",
        date: "12 Aprile 2024",
        author: "Redazione",
        readTime: "4 min",
        excerpt: "Viviamo in un ecosistema: cittadini, aziende, istituzioni, tecnologie. Nessuno è fuori.",
        content: `
            <p>Viviamo in un ecosistema: cittadini, aziende, istituzioni, tecnologie. Nessuno è fuori. Il rischio non è un incidente isolato; è un fenomeno sistemico. E se è sistemico, anche la risposta deve esserlo.</p>
            <p>La responsabilità, allora, diventa un patto. Le imprese devono progettare e proteggere, senza scaricare sul cliente l’onere di capire tutto. Le istituzioni devono creare regole chiare e applicabili, senza inseguire la tecnologia come fosse sempre in ritardo di un passo. I cittadini devono essere alfabetizzati, non per diventare esperti, ma per diventare liberi.</p>
            <p>L’AI non è un attore morale. Non ha coscienza, non ha colpa, non ha merito. È un moltiplicatore. Moltiplica il bene quando è guidata bene. Moltiplica il danno quando è guidata male, o quando non è guidata affatto.</p>
            <p>Cyberumanità vuole essere uno spazio di costruzione: Atlante, strumenti, articoli, e soprattutto una comunità che coltiva la domanda giusta. Perché il rischio non si elimina. Si governa. E governare significa capire, discutere, scegliere.</p>
            <p>Se sai, non hai paura. Non perché il mondo diventa semplice, ma perché smette di essere oscuro. E quando qualcosa non è oscuro, puoi affrontarlo. Puoi dargli un nome. Puoi trovare una direzione. Puoi restare umano.</p>
        `
    }
];

function stripHtml(html: string) {
    let tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
}

export const fetchArticles = async (): Promise<BlogPost[]> => {
    try {
        const res = await fetch(`${wpConfig.API_URL}/posts?_embed&per_page=12`);
        if (!res.ok) throw new Error("Failed to fetch posts");
        const data = await res.json();
        
        return data.map((post: any) => {
            const dateObj = new Date(post.date);
            const formattedDate = dateObj.toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' });
            
            const categoryName = post._embedded?.['wp:term']?.[0]?.[0]?.name || "Generale";
            const authorName = post._embedded?.['author']?.[0]?.name || "Redazione";
            const textContent = stripHtml(post.content.rendered);
            const wordCount = textContent.split(/\s+/).length;
            const readTime = Math.ceil(wordCount / 200) + " min";

            return {
                id: post.id,
                category: categoryName,
                title: post.title.rendered,
                date: formattedDate,
                excerpt: stripHtml(post.excerpt.rendered).slice(0, 150) + "...",
                author: authorName,
                readTime: readTime,
                content: post.content.rendered
            };
        });
    } catch (e) {
        // FALLBACK TO STATIC ARTICLES IF WP FAILS
        return STATIC_ARTICLES;
    }
};

export const fetchArticleById = async (id: number): Promise<BlogPost | undefined> => {
    // 1. TRY WP API FIRST
    try {
        const res = await fetch(`${wpConfig.API_URL}/posts/${id}?_embed`);
        if (res.ok) {
            const post = await res.json();
            const dateObj = new Date(post.date);
            const formattedDate = dateObj.toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' });
            const categoryName = post._embedded?.['wp:term']?.[0]?.[0]?.name || "Generale";
            const authorName = post._embedded?.['author']?.[0]?.name || "Redazione";
            const textContent = stripHtml(post.content.rendered);
            const wordCount = textContent.split(/\s+/).length;
            const readTime = Math.ceil(wordCount / 200) + " min";

            return {
                id: post.id,
                category: categoryName,
                title: post.title.rendered,
                date: formattedDate,
                excerpt: stripHtml(post.excerpt.rendered),
                author: authorName,
                readTime: readTime,
                content: post.content.rendered
            };
        }
    } catch(e) {
        // Ignore error and proceed to static check
    }

    // 2. CHECK STATIC DATABASE
    const staticPost = STATIC_ARTICLES.find(p => p.id === id);
    if (staticPost) return staticPost;

    return undefined;
};
